import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { API, DeviceFullDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from '../../../../services/util.service';
import { DeviceService } from '../../../../services/device/deviceService';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../../services/global/RequestClass';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { RoutingID } from '../../../../routes/sb-routing-enum';

type SplitButtonAction = 'details';

@Component({
  selector: 'app-location-devices',
  templateUrl: './location-devices.component.html',
  styleUrls: ['./location-devices.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationDevicesComponent extends RequestClass {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly deviceService = inject(DeviceService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() locationId: number;

  // table wrapper variables
  devices: DeviceFullDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.locationId || !this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ss_fm_Location_retrieveDevicesByLocationId(
        this.locationId,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (data: DeviceFullDTO[]) => {
          this.devices = this.buildSplitButtons(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  getLocationId() {
    const arr = [];
    if (this.locationId) {
      arr.push(this.locationId);
      return JSON.stringify(arr);
    }
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.device_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.openDetails',
          link: RoutingID.deviceDetails,
          pathParams: [{ id: 'deviceID', value: item?.id }],
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
